// JQ4-38 · US-PAGOS-004 — Cash payment schemas (shared between backend + api-client)
//
// Request: amountCents must be a positive integer (400 if violated — before business logic).
// Response: updated ticket fields sufficient for the PWA to apply an optimistic update.

import { z } from 'zod';

// ── RegisterCashPaymentRequest ────────────────────────────────────────────────

export const RegisterCashPaymentRequestSchema = z.object({
  /**
   * Amount tendered in cents. Must be a positive integer.
   * Validated by the Zod pipe at the HTTP boundary BEFORE the use case executes.
   * A value of 0 or negative → 400 Bad Request.
   * A zero-fare ticket (totalCents === 0) → 422 Unprocessable Entity from the UC.
   */
  amountCents: z.number().int().positive(),
});

export type RegisterCashPaymentRequest = z.infer<typeof RegisterCashPaymentRequestSchema>;

// ── RegisterCashPaymentResponse ───────────────────────────────────────────────

export const RegisterCashPaymentResponseSchema = z.object({
  /** Ticket UUID */
  id: z.string().uuid(),
  ticketNumber: z.number().int().positive(),
  /** Always 'paid' on success */
  paymentStatus: z.literal('paid'),
  /** Always 'cash' on success */
  paymentMethod: z.literal('cash'),
  /** ISO-8601 timestamp of when the cash payment was registered */
  paidAt: z.string().datetime(),
});

export type RegisterCashPaymentResponse = z.infer<typeof RegisterCashPaymentResponseSchema>;

// ── TicketAmountDueResponse (QA-oct I8) ───────────────────────────────────────

/** GET /valet/tickets/:id/amount-due — server-computed charge shown in the cash dialog. */
export const TicketAmountDueResponseSchema = z.object({
  amountDueCents: z.number().int().nonnegative(),
  currency: z.string(),
  paymentStatus: z.enum(['unpaid', 'paid']),
});

export type TicketAmountDueResponse = z.infer<typeof TicketAmountDueResponseSchema>;
